import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../contexts/AuthContext";
import FormInput from "../../../components/controls/FormInput";
import Autocomplete from "../../../components/Places/PlacesAutocomplete";
import Toggle from "../../../components/ui/Toggle";
import {
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import Button from "../../../components/controls/Button";

const MyPublicProfile = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    displayName: "",
    location: null,
    phoneNumber: "",
    email: "",
    availability: "",
    code: "",
    isActive: false,
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { currentUser } = useAuth();

  const [copied, setCopied] = React.useState(false);
  const code = form?.code?.trim();
  const url = code ? `https://ananassolutions.ca/representant/${code}` : "";

  useEffect(() => {
    if (!currentUser?.id) return;
    fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/Representative/profile/${currentUser.id}`
    )
      .then((res) => res.json())
      .then(setForm)
      .catch(console.error);
  }, [currentUser?.id]);

  const handleChange = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSave = async () => {
    console.log("Saving profile", form);
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/Representative/profile/${currentUser.id}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      }
    );
    if (res.ok) setSuccess(true);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // petit fallback si clipboard n'est pas dispo
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 text-primary">
      <h1 className="text-2xl font-bold mb-4">
        {t("global.myPublicProfile")} {form.code && `(${form.code})`}
      </h1>

      <div className="flex items-center gap-2 mb-4 border p-2">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[#3a3a3a] hover:underline"
          title="Ouvrir la page publique du représentant"
        >
          <span className="">{url}</span>
          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
        </a>

        <Button
          as="button"
          type="button"
          onClick={copyToClipboard}
          color={copied ? "mint" : "primary"} // feedback visuel
          size="sm"
          leftIcon={
            copied ? (
              <CheckIcon className="h-4 w-4" />
            ) : (
              <ClipboardDocumentIcon className="h-4 w-4" />
            )
          }
          className="px-3 py-1" // compact chip-like
          title="Copier l'URL"
          aria-live="polite"
        >
          {copied ? "Copié" : "Copier"}
        </Button>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        Ces informations ne sont{" "}
        <strong>pas liées à vos produits ou à votre compagnie</strong>. Elles
        serviront à votre représentation et seront{" "}
        <strong>affichées publiquement </strong>
        pour permettre aux clients de vous contacter en cas de besoin. Si vous
        ne souhaitez pas être contacté, vous pouvez désactiver votre profil
        public à l'aide du bouton ci-dessous.
      </p>
      <p className="text-sm text-gray-600 mb-4 border-b pb-4">
        {" "}
        <strong>
          *Pour des raisons de sécurité, nous vous recommandons d’utiliser une
          adresse courriel dédiée à votre rôle de représentant pour Ananas
          Solutions. Cette adresse, utilisée pour l’affichage public et les
          échanges avec les clients, peut être différente de celle que vous
          utilisez pour vous connecter à notre site web.
        </strong>
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          ["firstName", "Prénom"],
          ["lastName", "Nom"],
          ["phoneNumber", "Téléphone"],
          ["email", "Courriel"],
        ].map(([key, label]) => (
          <div key={key}>
            <label className="block font-medium mb-1">{label}</label>
            <FormInput
              name={key}
              value={form[key] || ""}
              onChange={(name, value) => handleChange(name, value)}
              placeholder={t(`recruitment.${key}`)}
              showRequiredOrOptional={false}
            />
          </div>
        ))}

        {/* Champ City avec Autocomplete */}
        <div>
          <label className="block font-medium mb-1">
            {t("representatives.fields.address")}
          </label>
          <Autocomplete
            value={form.city || ""}
            placeholder={t("stepOne.location.placeholder")}
            onSelect={(e) => {
              handleChange("location", e); // contient lat/lng
              handleChange("city", e.formattedAddress); // ex: "Chambly, QC"
            }}
            initialAddress={form.city || ""}
            showTitle={false}
          />
        </div>
        <div className="flex justify-start items-center mt-4">
          <Toggle
            isEnabled={!!form.isActive}
            onToggle={(e) => handleChange("isActive", e.target.checked)}
            labelOn={t("representatives.visible")}
            labelOff={t("representatives.hidden")}
            colorActive="bg-green-500"
            colorInactive="bg-red-500"
            className="toggle-lg"
            title={t("representatives.visibilityToggle")}
          />
        </div>
      </div>
      <div className="mt-6 text-center">
        <Button
          as="button"
          type="button"
          onClick={handleSave}
          color="primary"
          size="md"
          disabled={loading}
          className="px-6 py-2" // optionnel: ton Button gère déjà le padding via size
        >
          {t("global.save")}
        </Button>
        {success && (
          <p className="text-green-600 mt-2">
            {t("representatives.publicProfile.success")}
          </p>
        )}
      </div>
    </div>
  );
};

export default MyPublicProfile;
